
import React from "react";
import Sidebar from "./Sidebar";
import { useIsMobile, useIsTablet } from "@/hooks/use-mobile";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { PanelLeft } from "lucide-react";
import ScrollToTop from "@/components/ui/scroll-to-top";

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  // Scroll to top on route change
  useScrollToTop();

  // Mobile and Tablet view with collapsed sidebar
  if (isMobile || isTablet) {
    return (
      <SidebarProvider>
        <div className="min-h-screen bg-background flex flex-col flex-1">
          {/* Fixed header bar with proper safe area handling for PWA */}
          <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border/20 pwa-header">
            <div className="flex items-center h-16 px-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(true)}
                className="h-10 w-10"
              >
                <PanelLeft className="h-5 w-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </div>
          </header>

          <Sidebar open={sidebarOpen} onOpenChange={setSidebarOpen} />
          
          {/* Main content with proper spacing and safe area handling for PWA */}
          <main id="main" className="flex-1 flex flex-col pwa-main-content">
            <div className="flex-1 container overflow-y-auto pb-safe-bottom">
              <div className="py-6">
                {children}
              </div>
            </div>
          </main>

          {/* Scroll to Top Button */}
          <ScrollToTop />
        </div>
      </SidebarProvider>
    );
  }

  // Desktop view with full sidebar
  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <Sidebar />
        <SidebarInset>
          <main id="main" className="flex-1 container overflow-y-auto">
            {children}
          </main>

          {/* Scroll to Top Button */}
          <ScrollToTop />
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default MainLayout;
